import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import useAxios from "../../../utils/useAxios";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { SmoothAvatar } from "../../../components/SmoothAvatar";
import { SquareAvatar } from "../../../components/images/SquareAvatar";
import TruncateFade from "../../../components/TruncateFade";
import { COUNTRY_OPTIONS, handleItemClick, LEVEL_OPTIONS } from "../../../constants";
import { getOptionName } from "../../../utils/getOptionName";
import PageLoader from "../../../components/PageLoader";
import useTournamentSocket from "../../../hooks/useTournamentSocket";
import { useTheme } from "../../../contexts/ThemeContext";
import { CompactTableRow } from "../../../components/CompactTableRow";
import { chipsMatchEntity, searchStateClass } from "../../../utils/searchMatch";
import { publishTournamentSearchOptions } from "../../../utils/tournamentSearchOptionsBus";
import { normalizeTeamPlayers } from "../../../utils/normalizeTeamPlayers";

const TournamentRatingPlayers = ({ tournamentGrade, refreshTrigger, ratingMode, teamSize, is_authorized, onOpenRatingTable, viewMode, hasRatingBase, tournament, searchChips = [] }) => {
    const { t } = useTranslation();
    const api = useAxios();
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    // Compact Table is a user-facing-only density preference - an admin
    // editing this tournament still gets the regular view regardless of the
    // stored setting, only seeing it once they switch to Public View for
    // this tournament. See Header.js/BottomNav.js for the matching
    // toggle-visibility gate.
    const { compactTable: rawCompactTable } = useTheme();
    const compactTable = rawCompactTable && !is_authorized;

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const ordering = "-tournament_score";

    const [nextUrl, setNextUrl] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const loadingRef = useRef(false);
    // Guards fetchRating against out-of-order responses: it re-fires
    // whenever `refreshTrigger` changes (e.g. after a rating settings save/
    // reset) - without this, a slower pre-change response can land after a
    // newer one and silently overwrite the freshly-reloaded rating list
    // with the old one.
    const ratingReqRef = useRef(0);
    // Scrolls the first highlighted card/row into view - queried off the DOM
    // (searchStateClass's own ".search-match", set on whichever card/row
    // just matched below) rather than tracked through the render functions'
    // own props. Same technique as TournamentPlayers.js's own version of
    // this effect.
    const lastScrolledMatchRef = useRef(null);
    useEffect(() => {
        if (searchChips.length === 0) {
            lastScrolledMatchRef.current = null;
            return;
        }
        const match = document.querySelector("#results-block .search-match");
        if (match && match !== lastScrolledMatchRef.current) {
            lastScrolledMatchRef.current = match;
            match.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [searchChips, items]);

    const isTeam = tournamentGrade > 1;

    // true/false once a chip is active, null while there are none (see
    // searchMatch.js's own contract) - a team matches on its own name OR
    // any of its players', same rule Bracket.js/TournamentGroupTable.js use.
    const matchOf = useCallback((item) => {
        if (!item) return null;
        return chipsMatchEntity(searchChips, { names: isTeam ? [item.name, ...(item.players || [])] : [item] });
    }, [isTeam, searchChips]);

    // Mirrors TournamentPlayers.js's own search: matching only happens
    // against what's already loaded (this endpoint has no server-side
    // search param), so while typing keeps pulling further infinite-scroll
    // pages until a match turns up or pagination runs out - otherwise a
    // player past the first page would look like they don't exist (or, now
    // that a match highlights in place rather than filtering the rest out,
    // simply never get the chance to highlight at all).
    useEffect(() => {
        if (searchChips.length === 0 || !nextUrl || loadingRef.current) return;
        const isFound = items.some(item => matchOf(item) === true);

        if (!isFound) {
            loadingRef.current = true;
            setLoadingMore(true);
            api.get(nextUrl).then(res => {
                setItems(prev => [...prev, ...(res.data.results || [])]);
                setNextUrl(res.data.next ? res.data.next.replace(/^https?:\/\/[^/]+/, '') : null);
            }).catch(e => {
                console.error(e);
            }).finally(() => {
                loadingRef.current = false;
                setLoadingMore(false);
            });
        }
    }, [searchChips, items, nextUrl, matchOf]);

    // Player-name chip options for the global search box - a dedicated
    // fetch, not built off the paginated `items` state above, for the same
    // reason as TournamentPlayers.js's own version of this effect: `items`
    // only holds whichever page(s) infinite scroll has loaded so far, which
    // left a participant seated further down the table with no suggestion
    // while typing until you'd already scrolled to them. Pulls the whole
    // roster in one shot instead, capped at player_quantity.
    useEffect(() => {
        if (!id || !tournament?.player_quantity) return;
        let cancelled = false;
        api.get(`/api/v1.0/tournaments/${id}/players-rating/?grade=${tournamentGrade}&limit=${tournament.player_quantity}`).then(res => {
            if (cancelled) return;
            const all = res.data.results || res.data || [];
            const names = new Map();
            const addName = (p) => {
                if (p?.last_name) {
                    const full = `${p.last_name} ${p.first_name || ''}`.trim();
                    names.set(full, full);
                }
            };
            if (isTeam) {
                all.forEach(item => {
                    if (!item) return;
                    if (item.name) names.set(item.name, item.name);
                    (item.players || []).forEach(addName);
                });
            } else {
                all.forEach(addName);
            }
            publishTournamentSearchOptions(
                names.size > 0
                    ? [{ label: t("Players"), options: Array.from(names.values()).map(n => ({ value: `p_${n}`, label: n, type: 'player' })) }]
                    : []
            );
        }).catch(e => console.error(e));
        return () => { cancelled = true; };
    }, [id, tournamentGrade, tournament?.player_quantity, isTeam, t]);

    useTournamentSocket(id, (payload) => {
        if (payload?.wake_up) {
            fetchRating();
        }
    });

    useEffect(() => {
        if (id && tournamentGrade) fetchRating();
    }, [id, ordering, tournamentGrade, refreshTrigger]);

    const fetchRating = async (order = ordering) => {
        setLoading(true);
        loadingRef.current = true;
        ratingReqRef.current += 1;
        const currentReq = ratingReqRef.current;
        try {
            const isExport = new URLSearchParams(location.search).get("export") === "true";
            let url = `/api/v1.0/tournaments/${id}/players-rating/?grade=${tournamentGrade}&ordering=${order}`;
            if (isExport) {
                url += `&limit=100`;
            }
            // players-rating/ doesn't annotate per-player `rating` on either
            // path (TournamentPlayerRatingSerializer for singles, or the
            // team branch's members prefetch) - singles is also missing
            // city/country/level outright. Used to cross-reference this from
            // the tournament's own teams/ roster for the team branch, but
            // that endpoint itself always returns rating:0 for every member
            // (backend bug, see backend-tasks/2026-10-20-teams-players-
            // rating-field-zero.md and the earlier 2026-09-12 one) - the flat
            // /players/ endpoint has the real value straight away for BOTH
            // branches, so always cross-reference against that one instead.
            // Fired alongside the ratings request (not awaited after it) -
            // neither depends on the other's result, so awaiting them in
            // sequence was just doubling this tab's load time against
            // Участники's single-request fetch.
            const profileUrl = `/api/v1.0/tournaments/${id}/players/?limit=100`;
            const [res, profileRes] = await Promise.all([
                api.get(url),
                api.get(profileUrl).catch(e => { console.error(e); return null; })
            ]);
            if (ratingReqRef.current !== currentReq) return;
            let results = res.data.results || res.data || [];

            if (profileRes) {
                const profileResults = profileRes.data.results || profileRes.data || [];
                const profileMap = new Map(
                    profileResults.filter(Boolean).map(p => [String(p.player_id || p.id), p])
                );

                if (isTeam) {
                    results = results.map(item => ({
                        ...item,
                        players: (item.players || []).map(p => {
                            const profile = profileMap.get(String(p?.player_id));
                            if (!p || !profile) return p;
                            return {
                                ...p,
                                city: p.city ?? profile.city,
                                country: p.country ?? profile.country,
                                level: p.level ?? profile.level,
                                rating: p.rating ?? profile.rating
                            };
                        })
                    }));
                } else {
                    results = results.map(item => {
                        const profile = profileMap.get(String(item.player_id));
                        if (!profile) return item;
                        return {
                            ...item,
                            city: item.city ?? profile.city,
                            country: item.country ?? profile.country,
                            level: item.level ?? profile.level,
                            rating: item.rating ?? profile.rating
                        };
                    });
                }
            }

            setItems(results);
            setNextUrl(res.data.next ? res.data.next.replace(/^https?:\/\/[^/]+/, '') : null);
        } catch (e) {
            console.error(e);
        } finally {
            // Only the still-current request may clear loading/initialLoad -
            // a stale request (superseded by a newer fetchRating call, e.g.
            // the refetch triggered by rating-settings loading right after
            // mount) must not flip these back to false while the winning
            // request is still in flight, or the empty-state message flashes
            // before the real data arrives.
            if (ratingReqRef.current === currentReq) {
                setLoading(false);
                setInitialLoad(false);
                loadingRef.current = false;
            }
        }
    };

    const observer = useRef();
    const lastElementRef = useCallback(node => {
        if (loadingRef.current) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && !!nextUrl && !loadingRef.current) {
                loadingRef.current = true;
                setLoadingMore(true);

                api.get(nextUrl).then(res => {
                    setItems(prev => [...prev, ...(res.data.results || [])]);
                    setNextUrl(res.data.next ? res.data.next.replace(/^https?:\/\/[^/]+/, '') : null);
                }).catch(e => {
                    console.error(e);
                }).finally(() => {
                    loadingRef.current = false;
                    setLoadingMore(false);
                });
            }
        }, { rootMargin: "100px" });

        if (node) observer.current.observe(node);
    }, [nextUrl]);

    if (initialLoad) {
        return <PageLoader />
    }

    if (items.length === 0) {
        return <div className="admin-wrapper" id="results-block">{t("No participants found")}</div>;
    }

    const getPlace = (item) =>
        item?.tournament_place && item.tournament_place !== 0 && item.tournament_place !== "0"
            ? item.tournament_place
            : t("-");

    const renderStatLabel = (full, short) => (
        <>
            <span className="rating-label-full">{full}</span>
            <span className="rating-label-short">{short}</span>
        </>
    );

    const renderStats = (item) => (
        <>
            <span className="entity-card-stat rating-stat-lead" style={{ color: 'var(--easy-black)' }}>{renderStatLabel(t("Matches"), t("M"))}: {item.tournament_matches || 0}</span>
            <span className="entity-card-stat" style={{ color: 'var(--primary-green)' }}>{renderStatLabel(t("Wins"), t("W"))}: {item.tournament_wins || 0}</span>
            <span className="entity-card-stat" style={{ color: 'var(--secondary-color)' }}>{renderStatLabel(t("Losses"), t("L"))}: {item.tournament_losses || 0}</span>
        </>
    );

    const renderSubtitle = (p) => {
        if (!p.city && !p.country && !p.level) return null;

        return (
            <div className="entity-card-subtitle">
                {p.city}
                {p.city && p.country && ", "}
                {p.country && t(getOptionName(COUNTRY_OPTIONS, p.country))}
                {(p.city || p.country) && p.level && ", "}
                {p.level && t(getOptionName(LEVEL_OPTIONS, p.level))}
            </div>
        );
    };

    // Same square-avatar tile as Участники's own Grid view (PlayersGridView) -
    // reuses its exact classes (players-grid/player-grid-card/mini-name, and
    // now also team-card-header/team-card-number/team-card-header-right, the
    // same header classes Участники's grid uses for the team card) so
    // switching sub-tabs in Grid mode doesn't jump between two different
    // card languages. Place stands in for the roster view's "#N" slot
    // number, and Points (when ratingMode is on) stands in for the roster
    // view's rating badge - M/W/L don't fit this compact a tile and stay
    // List-only, same as city/country/level already are.
    const gridSlotSize = isTeam ? (tournamentGrade === 2 ? 2 : (teamSize || 1)) : 1;
    // Same number, except a grade 3 tournament whose team_size isn't set yet
    // falls back to "no cap" (0) instead of gridSlotSize's 1 - normalizing a
    // roster against that 1 would hide real team members, while leaving it
    // uncapped still drops the duplicate-slot member this is here for.
    const rosterSlotSize = tournamentGrade === 2 ? 2 : (teamSize || 0);

    // Grid counterpart of renderScore - read-only badge, same as the list
    // view.
    const renderGridScore = (participantId, currentScore) => {
        if (!ratingMode) return null;

        return (
            <span className="team-card-rating">
                <FeatherIcon icon="bar-chart-2" size={12} /><span style={{ color: 'var(--primary-green)' }}>+</span>{currentScore}
            </span>
        );
    };

    const renderGridCard = (item, index) => {
        const isLastLoaded = items.length === index + 1;
        const participantId = item.player_id;
        const currentScore = item.tournament_score ?? 0;
        const isClickable = !is_authorized;

        return (
            <div
                className={`player-grid-card ${searchStateClass(matchOf(item))}`}
                key={item.player_id || index}
                ref={isLastLoaded ? lastElementRef : null}
                onClick={() => handleItemClick(item, history, location)}
                style={isClickable ? { cursor: "pointer" } : { cursor: "default" }}
            >
                <div className="team-card-header rating-card-header no-line">
                    <div className="team-card-header-left">
                        <span className="team-card-number">{getPlace(item)} {t("Place")}</span>
                    </div>
                    {ratingMode && (
                        <div className="team-card-header-right">
                            {renderGridScore(participantId, currentScore)}
                        </div>
                    )}
                </div>
                <table style={{ width: '100%', tableLayout: 'fixed' }}>
                    <tbody>
                    <tr>
                        <td style={{ position: 'relative' }}>
                            <SquareAvatar src={item.image} />
                            <div className="mini-name black-name">
                                <TruncateFade text={item.last_name} className="uppercase" />
                                <br />
                                <TruncateFade text={item.first_name} />
                            </div>
                            {hasRatingBase && item.rating != null && (
                                <div className="grid-rating grid-rating-player">
                                    <FeatherIcon icon="bar-chart-2" size={12} />{item.rating}
                                </div>
                            )}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const renderGridCardTeam = (item, index) => {
        // Capped at the tournament's own slot count (and one player per slot):
        // this card's table has exactly --slot-size columns, so a corrupted
        // pair's third member used to both show up as a third participant and
        // squeeze the row. See src/utils/normalizeTeamPlayers.js.
        const teamPlayers = normalizeTeamPlayers(item?.players, rosterSlotSize);
        const isLastLoaded = items.length === index + 1;
        const participantId = item.id;
        const currentScore = item.tournament_score ?? 0;

        return (
            <div className={`player-grid-card ${searchStateClass(matchOf(item))}`} key={item.id || index} ref={isLastLoaded ? lastElementRef : null}>
                <div className="team-card-header rating-card-header no-line">
                    <div className="team-card-header-left">
                        <span className="team-card-number">{getPlace(item)} {t("Place")}</span>
                        {tournamentGrade === 3 && (
                            <span className="team-name-display">{item.name || ""}</span>
                        )}
                    </div>
                    {ratingMode && (
                        <div className="team-card-header-right">
                            {renderGridScore(participantId, currentScore)}
                        </div>
                    )}
                </div>
                <table style={{ width: '100%', tableLayout: 'fixed' }}>
                    <tbody>
                    <tr>
                        {teamPlayers.map((p, i) => {
                            const isClickable = !is_authorized && tournamentGrade < 3;
                            return (
                                <td
                                    key={i}
                                    style={{ position: 'relative', cursor: isClickable ? "pointer" : "default" }}
                                    onClick={isClickable ? () => handleItemClick(p, history, location) : undefined}
                                >
                                    <SquareAvatar src={p?.image} />
                                    <div className="mini-name black-name">
                                        <TruncateFade text={p?.last_name} className="uppercase" />
                                        <br />
                                        <TruncateFade text={p?.first_name} />
                                    </div>
                                    {hasRatingBase && p?.rating != null && (
                                        <div className="grid-rating grid-rating-player">
                                            <FeatherIcon icon="bar-chart-2" size={12} />{p.rating}
                                        </div>
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    // Compact Table Mode - singles. Same shape as TournamentPlayers.js's own
    // renderCompactSlotRow, place instead of slot number, Points (when
    // ratingMode is on) instead of the roster row's rating badge in
    // `actions` - M/W/L stay List-card-only ("прячем, а не сжимаем", see
    // frontend-tasks/2026-09-02-compact-table-mode.md §6 - no room for 3
    // more icon+value stats in one action column, so they're dropped
    // entirely here rather than compressed).
    const renderCompactSingle = (item, index, zebra) => {
        const p = item;
        const participantId = item.player_id;
        const currentScore = item.tournament_score ?? 0;
        const isLastLoaded = items.length === index + 1;

        const subtitle = [
            p.city,
            p.country && t(getOptionName(COUNTRY_OPTIONS, p.country)),
            p.level && t(getOptionName(LEVEL_OPTIONS, p.level))
        ].filter(Boolean).join(", ");

        return (
            <CompactTableRow
                key={item.player_id || index}
                ref={isLastLoaded ? lastElementRef : null}
                variant="tournament-players"
                zebra={zebra}
                className={searchStateClass(matchOf(item))}
                indexLabel={getPlace(item)}
                avatarSrc={p.image}
                title={<>{p.last_name}{p.first_name && <>, {p.first_name}</>}</>}
                subtitle={subtitle}
                actions={ratingMode ? (
                    <span className="row-stat"><span style={{ color: 'var(--primary-green)' }}>+</span>{currentScore}</span>
                ) : (hasRatingBase && p.rating != null && (
                    <span className="row-stat cursor" onClick={() => onOpenRatingTable?.(participantId)}>
                        <FeatherIcon icon="bar-chart-2" />{p.rating}
                    </span>
                ))}
                onClick={() => handleItemClick(p, history, location)}
            />
        );
    };

    // Compact Table Mode - pairs/teams. Same nested shape as
    // TournamentPlayers.js's own renderCompactTeam (.compact-team/-header/
    // -players), place+team name in the header instead of #number, Points
    // instead of the total-rating stat.
    const renderCompactRatingTeam = (item, index, zebra) => {
        const rowPlayers = normalizeTeamPlayers(item.players, rosterSlotSize);
        const currentScore = item.tournament_score ?? 0;
        const isLastLoaded = items.length === index + 1;

        return (
            <div className={`compact-team${zebra ? " trow-odd" : ""} ${searchStateClass(matchOf(item))}`} key={item.id || index} ref={isLastLoaded ? lastElementRef : null}>
                <div className="compact-team-header">
                    <span className="compact-team-header-left">
                        <span className="compact-team-number">{getPlace(item)}</span>
                        {tournamentGrade === 3 && (
                            <span className="compact-team-name">{item.name || ""}</span>
                        )}
                    </span>
                    {ratingMode && (
                        <span className="compact-team-header-right">
                            <span className="row-stat"><span style={{ color: 'var(--primary-green)' }}>+</span>{currentScore}</span>
                        </span>
                    )}
                </div>
                <div className="compact-team-players">
                    {rowPlayers.map((p, i) => (
                        <div
                            className="compact-team-player"
                            key={i}
                            onClick={() => handleItemClick(p, history, location)}
                        >
                            <span className="trow-avatar">
                                {p?.image && <img src={p.image} alt="" />}
                            </span>
                            <span className="trow-name-text">
                                {p?.last_name}
                                {p?.first_name && <>, {p.first_name}</>}
                                {(p?.city || p?.country || p?.level) && (
                                    <span className="trow-subtitle"> · {[
                                        p.city,
                                        p.country && t(getOptionName(COUNTRY_OPTIONS, p.country)),
                                        p.level && t(getOptionName(LEVEL_OPTIONS, p.level))
                                    ].filter(Boolean).join(", ")}</span>
                                )}
                            </span>
                            {hasRatingBase && p?.rating != null && (
                                <span
                                    className="row-stat cursor"
                                    onClick={(e) => { e.stopPropagation(); onOpenRatingTable?.(p.player_id); }}
                                >
                                    <FeatherIcon icon="bar-chart-2" />{p.rating}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <>
        {viewMode === "grid" ? (
            <div id="results-block">
                <div className={`players-grid${isTeam ? "" : " players-grid-singles"}`} style={{ '--slot-size': gridSlotSize }}>
                    {items.map((item, index) => isTeam ? renderGridCardTeam(item, index) : renderGridCard(item, index))}
                    {loadingMore && <div style={{ textAlign: "center", padding: "10px", color: "var(--secondary-color)" }}>{t("Loading...")}</div>}
                </div>
            </div>
        ) : (
        <div className={compactTable ? "" : "admin-wrapper"} id="results-block">
            {isTeam ? (
                <div className={compactTable ? "compact-teams" : "team-table tournament-players-table"}>
                    {compactTable ? (() => {
                        let compactRowIndex = 0;
                        return items.map((item, index) => renderCompactRatingTeam(item, index, compactRowIndex++ % 2 === 1));
                    })() : items.map((item, index) => {
                        const rowPlayers = normalizeTeamPlayers(item.players, rosterSlotSize);
                        const currentScore = item.tournament_score ?? 0;
                        const isLastLoaded = items.length === index + 1;

                        return (
                            <div className={`team-card rating-card ${searchStateClass(matchOf(item))}`} key={item.id || index} ref={isLastLoaded ? lastElementRef : null}>
                                <div className="team-card-header rating-card-header dashed-line">
                                    <div className="team-card-header-left">
                                        <span className="team-card-number">{getPlace(item)} {t("Place")}</span>
                                        {tournamentGrade === 3 && (
                                            <span className="team-name-display">{item.name || ""}</span>
                                        )}
                                    </div>
                                    {renderStats(item)}
                                </div>

                                {rowPlayers.map((p, i) => {
                                    const isClickable = !is_authorized && tournamentGrade < 3;

                                    return (
                                        <div
                                            className="team-player-row-full"
                                            key={i}
                                            onClick={() => handleItemClick(p, history, location)}
                                            style={isClickable ? { cursor: "pointer" } : { cursor: "default" }}
                                        >
                                            <div className="team-player-avatar">
                                                <SmoothAvatar src={p.image} name={`${p.first_name || ""} ${p.last_name || ""}`.trim()} />
                                            </div>
                                            <div className="team-player-info">
                                                <span className="black-name">
                                                    <span className="uppercase">{p.last_name}</span>
                                                    {p.first_name && <>, {p.first_name}</>}
                                                </span>
                                                <span className="gray">
                                                    {p.city}
                                                    {p.city && p.country && ", "}
                                                    {p.country && t(getOptionName(COUNTRY_OPTIONS, p.country))}
                                                    {(p.city || p.country) && p.level && ", "}
                                                    {p.level && t(getOptionName(LEVEL_OPTIONS, p.level))}
                                                </span>
                                            </div>
                                            {ratingMode && (
                                                <span style={{ color: 'var(--primary-green)' }}>+{currentScore}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                    {loadingMore && <div style={{ textAlign: "center", padding: "10px", color: "var(--secondary-color)" }}>{t("Loading...")}</div>}
                </div>
            ) : (
                <div className={compactTable ? "trows" : "entity-cards"}>
                    {compactTable ? (() => {
                        let compactRowIndex = 0;
                        return items.map((item, index) => renderCompactSingle(item, index, compactRowIndex++ % 2 === 1));
                    })() : items.map((item, index) => {
                        const p = item;
                        const currentScore = item.tournament_score ?? 0;
                        const isLastLoaded = items.length === index + 1;
                        const isClickable = !is_authorized;

                        return (
                            <div
                                className={`entity-card rating-card ${searchStateClass(matchOf(item))}`}
                                key={item.player_id || index}
                                ref={isLastLoaded ? lastElementRef : null}
                                onClick={() => handleItemClick(p, history, location)}
                                style={isClickable ? { cursor: "pointer" } : { cursor: "default" }}
                            >
                                <div className="entity-card-header rating-card-header dashed-line">
                                    <span className="entity-card-number">{getPlace(item)} {t("Place")}</span>
                                    {ratingMode && (
                                        <span style={{ color: 'var(--primary-green)' }}>+{currentScore}</span>
                                    )}
                                    {renderStats(item)}
                                </div>

                                <div className="entity-card-top">
                                    <div className="entity-card-thumb">
                                        <SmoothAvatar src={p.image} name={`${p.first_name || ""} ${p.last_name || ""}`.trim()} />
                                    </div>
                                    <div className="entity-card-titles">
                                        <div className="entity-card-title">
                                            <span className="uppercase">{p.last_name}</span>
                                            {p.first_name && <>, {p.first_name}</>}
                                        </div>
                                        {renderSubtitle(p)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {loadingMore && <div style={{ textAlign: "center", padding: "10px", color: "var(--secondary-color)" }}>{t("Loading...")}</div>}
                </div>
            )}
        </div>
        )}
        </>
    );
};

export default TournamentRatingPlayers;
